import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getPlatformModules, disablePlatformModule, enablePlatformModule } from '../../services/api';
import { formatDate } from '../../utils/formatters';

const GLOBAL = 0;
const REASON_MAX = 500;

/* Причина отключения — обязательна: её видят пользователи вместо раздела */
const ModuleReasonModal = ({ title, initialReason, onSubmit, onClose }) => {
  const [reason, setReason] = useState(initialReason || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Укажите причину — её увидят пользователи организации');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось отключить раздел');
      setSaving(false);
    }
  };

  return (
    <div className="ui-modal" onClick={onClose} role="presentation">
      <div className="ui-modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ui-modal-head">
          <h2>{title}</h2>
          <button className="ui-icon-btn" type="button" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" />
            </svg>
          </button>
        </div>

        <form className="ui-form" onSubmit={handleSubmit}>
          <div className="ui-field">
            <label htmlFor="module-reason">Причина отключения</label>
            <textarea
              id="module-reason"
              rows={4}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(''); }}
              placeholder="Например: раздел на доработке, вернём до 15 октября"
              autoFocus
            />
            <span className="ui-dim">{reason.length} / {REASON_MAX} · показывается пользователям вместо раздела</span>
          </div>

          {error && <div className="ui-inline-error">{error}</div>}

          <div className="ui-modal-actions">
            <button type="button" className="ui-btn-ghost" onClick={onClose} disabled={saving}>
              Отмена
            </button>
            <button type="submit" className="ui-btn-danger" disabled={saving}>
              {saving ? 'Сохраняем…' : initialReason ? 'Сохранить причину' : 'Отключить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* Отключение разделов у одной организации или у всех сразу. Отключение
   постоянное — до тех пор, пока его не снимут здесь же. */
const ModulesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [scope, setScope] = useState(GLOBAL);
  const [editing, setEditing] = useState(null); // { page, label, reason }

  const load = useCallback(async () => {
    try {
      setData(await getPlatformModules());
      setError('');
    } catch (e) {
      setError('Не удалось загрузить список разделов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const orgName = useMemo(() => {
    const map = {};
    (data?.organizations || []).forEach((o) => { map[o.id] = o.name; });
    return (id) => (id === GLOBAL ? 'Все организации' : map[id] || `#${id}`);
  }, [data]);

  const pageLabel = useMemo(() => {
    const map = {};
    (data?.pages || []).forEach((p) => { map[p.key] = p.label; });
    return (key) => map[key] || key;
  }, [data]);

  const blockFor = (orgId, page) => (data?.blocks || []).find((b) => b.org_id === orgId && b.page === page);

  const handleDisable = async (page, reason) => {
    const res = await disablePlatformModule(scope, page, reason);
    setData((d) => ({ ...d, blocks: res.blocks }));
  };

  const handleEnable = async (orgId, page) => {
    setActionError('');
    try {
      const res = await enablePlatformModule(orgId, page);
      setData((d) => ({ ...d, blocks: res.blocks }));
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Не удалось включить раздел');
    }
  };

  if (loading) return <section className="ui-card"><LoadingSpinner /></section>;
  if (error) return <section className="ui-card"><div className="ui-inline-error">{error}</div></section>;

  const blocks = data.blocks || [];

  return (
    <>
      <section className="ui-card">
        <div className="ui-card-head">
          <h2>Сейчас отключено</h2>
          <p>Все действующие отключения. Раздел остаётся выключенным, пока его не включат здесь.</p>
        </div>
        {blocks.length === 0 && <div className="ui-empty">Все разделы включены</div>}
        {blocks.length > 0 && (
          <div className="platform-org-table">
            {blocks.map((b) => (
              <div className="platform-org-row" key={b.id}>
                <div className="platform-org-main">
                  <div className="platform-org-name">{pageLabel(b.page)} · {orgName(b.org_id)}</div>
                  <div className="platform-org-block-note">{b.reason}</div>
                  <div className="ui-dim">
                    {formatDate(b.disabled_at)}{b.disabled_by ? ` · ${b.disabled_by}` : ''}
                  </div>
                </div>
                <div className="platform-org-actions">
                  <button type="button" className="ui-btn-ghost" onClick={() => handleEnable(b.org_id, b.page)}>
                    Включить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="ui-card">
        <div className="ui-card-head">
          <h2>Разделы</h2>
          <p>Отключённый раздел пропадает у всех пользователей организации, включая администратора; вместо него — ваша причина. «Поддержка» не отключается.</p>
        </div>

        <div className="ui-form" style={{ marginBottom: 'var(--s-4)' }}>
          <div className="ui-field">
            <label htmlFor="modules-scope">Для кого</label>
            <select id="modules-scope" value={scope} onChange={(e) => setScope(Number(e.target.value))}>
              <option value={GLOBAL}>Все организации</option>
              {data.organizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>
              ))}
            </select>
          </div>
        </div>

        {actionError && <div className="ui-inline-error">{actionError}</div>}

        <div className="platform-org-table">
          {data.pages.map(({ key, label }) => {
            const own = blockFor(scope, key);
            const global = scope !== GLOBAL ? blockFor(GLOBAL, key) : null;
            return (
              <div className="platform-org-row" key={key}>
                <div className="platform-org-main">
                  <div className="platform-org-name">{label}</div>
                  {own && <div className="platform-org-block-note">{own.reason}</div>}
                  {!own && global && (
                    <div className="ui-dim">Отключён для всех организаций: {global.reason}</div>
                  )}
                </div>
                <span className={`ui-status ${own || global ? 'off' : 'on'}`}>
                  {own ? 'Отключён' : global ? 'Отключён для всех' : 'Включён'}
                </span>
                <div className="platform-org-actions">
                  {own ? (
                    <>
                      <button type="button" className="ui-btn-ghost"
                              onClick={() => setEditing({ page: key, label, reason: own.reason })}>
                        Причина
                      </button>
                      <button type="button" className="ui-btn-ghost" onClick={() => handleEnable(scope, key)}>
                        Включить
                      </button>
                    </>
                  ) : (
                    <button type="button" className="ui-btn-ghost"
                            onClick={() => setEditing({ page: key, label, reason: '' })}>
                      Отключить
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {editing && (
        <ModuleReasonModal
          title={`${editing.reason ? 'Причина' : 'Отключить'} «${editing.label}» — ${orgName(scope)}`}
          initialReason={editing.reason}
          onSubmit={(reason) => handleDisable(editing.page, reason)}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};

export default ModulesCard;
